import { AfterViewInit, Component, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { SelectionModel } from '@angular/cdk/collections';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-project-diagramation-export',
  templateUrl: './project-diagramation-export.component.html',
  styleUrls: ['./project-diagramation-export.component.scss'],
})
export class ProjectDiagramationExportComponent implements AfterViewInit {
  
  dataSource: MatTableDataSource<any>;
  selection = new SelectionModel<any>(true, []);
  projects: string[] = [];
  selectedProjects: string[] = [];
  private searchText = '';
  displayedColumns: string[] = ['select', 'id', 'alias', 'medio', 'pop', 'posicion', 'cant_sucursales', 'cantidad', 'nombre_proyecto'];

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<ProjectDiagramationExportComponent>
  ) {
    this.dataSource = new MatTableDataSource(this.data.projectDiagramations);
    this.projects = Array.from(new Set<string>(this.dataSource.data
      .map(row => row.nombre_proyecto)
      .filter((name): name is string => typeof name === 'string' && name.trim().length > 0)))
      .sort((a, b) => a.localeCompare(b));
    const textFilterPredicate = this.dataSource.filterPredicate;
    this.dataSource.filterPredicate = (row, filter) => {
      const { searchText, projects } = JSON.parse(filter);
      return projects.includes(row.nombre_proyecto)
        && textFilterPredicate(row, searchText);
    };
    this.applyProjectFilter(this.projects.filter(project => project === this.data.project.nombre));
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.dataSource.sort.sort({ id: 'id', start: 'asc', disableClear: false });
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.searchText = filterValue.trim().toLowerCase();
    this.updateFilter();
  }

  applyProjectFilter(projects: string[]): void {
    this.selectedProjects = projects;
    this.selection.selected.slice().forEach(row => {
      if (!projects.includes(row.nombre_proyecto)) {
        this.selection.deselect(row);
      }
    });
    this.updateFilter();
  }

  areAllProjectsSelected(): boolean {
    return this.projects.length > 0
      && this.projects.every(project => this.selectedProjects.includes(project));
  }

  toggleAllProjects(): void {
    this.applyProjectFilter(this.areAllProjectsSelected() ? [] : this.projects.slice());
  }

  private updateFilter(): void {
    this.dataSource.filter = JSON.stringify({ searchText: this.searchText, projects: this.selectedProjects });

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  isAllSelected() {
    return this.dataSource.filteredData.length > 0
      && this.dataSource.filteredData.every(row => this.selection.isSelected(row));
  }

  masterToggle() {
    if (this.isAllSelected()) {
      this.dataSource.filteredData.forEach(row => this.selection.deselect(row));
      return;
    }

    this.dataSource.filteredData.forEach(row => this.selection.select(row));
  }

  checkboxLabel(row?: any): string {
    if (!row) {
      return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.id + 1}`;
  }

  private getExportArray(value: any, field: string, diagramId: any): any[] {
    let parsedValue: any;
    try {
      parsedValue = typeof value === "string" ? JSON.parse(value) : value;
    } catch (error) {
      throw new Error("El campo " + field + " de la diagramación " + diagramId + " contiene un JSON inválido.");
    }

    if (!Array.isArray(parsedValue)) {
      throw new Error("El campo " + field + " de la diagramación " + diagramId + " debe contener una lista.");
    }
    return parsedValue;
  }

  exportDiagramaciones() {
    const totalSelected = this.selection.selected.length;
    Swal.fire({
      title: `¿Exportar ${totalSelected} diagramaciones?`,
      text: "Se generará un archivo JSON con la configuración de las diagramaciones seleccionadas.",
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Aceptar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        const selectedIds = this.selection.selected.map(s => s.id);
        const diagramationsToExport = this.data.projectDiagramations.filter((x: any) => selectedIds.includes(x.id));

        const exportObject = diagramationsToExport.map((item: any) => {
          const configVobos = this.getExportArray(item.config_vobos, "config_vobos", item.id).map((configPhase: any) => ({
            ...configPhase,
            name: this.data.phases.find((phase: any) => Number(phase.id) === Number(configPhase.id))?.name
          }));

          return {
            medio: item.medio,
            pop: item.pop,
            posicion: item.posicion,
            alias: item.alias,
            sucursales: item.pop_config[0]?.sucursales.map((x: any) => x.id) || [],
            config_vobos: configVobos,
            promociones: this.getExportArray(item.promociones, "promociones", item.id)
          };
        });

        const blob = new Blob([JSON.stringify(exportObject, null, 2)], {
          type: 'application/json',
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const projectNames = this.selectedProjects.length
          ? this.selectedProjects
          : Array.from(new Set<string>(diagramationsToExport.map((item: any) => item.nombre_proyecto).filter(Boolean)));
        link.download = `${this.data.campaing.name}_${projectNames.join('_') || this.data.project.nombre}_${new Date().getTime()}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        Swal.fire(
          '¡Exportado!',
          'El archivo de diagramaciones se ha generado correctamente. Se descargará automáticamente en tu navegador.',
          'success'
        );
        this.dialogRef.close();        
      }
    }).catch((error: any) => {
      console.error("Error al exportar diagramaciones:", error);
      Swal.fire("Error", error.message || "No se pudieron exportar las diagramaciones seleccionadas.", "error");
    });
  }
}
